import { createSelector } from '@reduxjs/toolkit'
import { uniq } from 'lodash-es'
import { ColorPicker } from 'react-aria-components'
import { Matrix4, Vector3 } from 'three'
import { cornerPoints } from '~/components/DrawingCanvas/util/boxDragUtil'
import { MetadataFields } from '~/components/MetadataFields'
import Panel, { PanelSection } from '~/components/UIKit/Panel'
import theme from '~/config/theme'
import { RootState } from '~/store'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { updateObjects } from '~/store/objects'
import { Obstruction } from '~/store/objects/types'
import { SELECTED_OBSTRUCTION_PANEL } from '~/store/panel/types'
import { Alert } from '~/ui/Alert'
import { ChooserWell } from '~/ui/Color'
import { DimensionInput } from '~/ui/DimensionInput'
import { Label } from '~/ui/Field'
import { NumberField } from '~/ui/NumberField'
import { Select, SelectItem } from '~/ui/Select'
import { camelToTitleCase } from '~/utils/string'

const selectSelectedObstructions = createSelector(
  [(state: RootState) => state.selectedObjects, (state: RootState) => state.objects.present.obstructions],
  (selected, obstructions) =>
    Object.values(obstructions)
      .filter(obstruction => selected.some(selectedObject => selectedObject.id === obstruction.id))
      .map(obstruction => ({ ...obstruction, extraData: cornerPoints(obstruction.positions, obstruction.height) }))
)

function uniqueBy<T, E>(items: T[], selector: (item: T) => E): E | null {
  const selected = uniq(items.map(selector))
  return selected.length === 1 ? selected[0] : null
}

export default function SelectedObstructionPanel() {
  const isTouchUI = useAppSelector(state => state.userInterface.isTouchUI)
  const selectedObstructions = useAppSelector(selectSelectedObstructions)
  const dispatch = useAppDispatch()

  const type = uniqueBy(selectedObstructions, it => it.obstructionType)
  const startingPosition = uniqueBy(selectedObstructions, it => it.startLocation)
  const rotation = uniqueBy(selectedObstructions, it => it.rotation.z)
  const offset = uniqueBy(selectedObstructions, it => it.offset ?? 0)
  const width = uniqueBy(selectedObstructions, it => it.extraData.width)
  const length = uniqueBy(selectedObstructions, it => it.extraData.length)
  const height = uniqueBy(selectedObstructions, it => it.height)
  const color = uniqueBy(selectedObstructions, it => it.color)

  const anyMixed =
    type === null ||
    startingPosition === null ||
    rotation === null ||
    offset === null ||
    width === null ||
    length === null ||
    height === null ||
    color === null

  const updateObstructions = (props: Partial<Obstruction>) => {
    dispatch(updateObjects(selectedObstructions.map(obstruction => ({ id: obstruction.id, ...props }))))
  }

  const updateDimensions = ({ width, length }: { width?: number; length?: number }) => {
    dispatch(
      updateObjects(
        selectedObstructions.map(obstruction => {
          const center = obstruction.position

          const goToOrigin = new Matrix4().makeTranslation(-center.x, -center.y, -center.z)
          const rescale = new Matrix4().makeScale(
            width ? width / obstruction.extraData.width : 1,
            length ? length / obstruction.extraData.length : 1,
            1
          )
          const goFromOrigin = new Matrix4().makeTranslation(center.x, center.y, center.z)

          const mtx = new Matrix4()

          mtx.multiply(goFromOrigin)
          mtx.multiply(rescale)
          mtx.multiply(goToOrigin)

          const vec = new Vector3()

          return {
            id: obstruction.id,
            positions: obstruction.positions.map(position => {
              vec.copy(position).applyMatrix4(mtx)

              return { x: vec.x, y: vec.y, z: vec.z }
            }),
          }
        })
      )
    )
  }

  return (
    <Panel
      title="Obstruction"
      alignment="right"
      docked
      scrollable
      panelKey={SELECTED_OBSTRUCTION_PANEL}
      hasToolbar={isTouchUI}
    >
      <PanelSection>
        <div className="flex flex-col gap-3">
          {type && type !== 'basic' && (
            <>
              <h3>{camelToTitleCase(type)}</h3>
              <img src={`/obstructions/${type}.png`} alt={camelToTitleCase(type)} />
            </>
          )}
          {anyMixed && (
            <Alert>Multiple obstructions are selected. Changes will apply to each selected obstruction.</Alert>
          )}
          <Select
            name="startingPosition"
            label="Starting Position"
            selectedKey={startingPosition}
            placeholder={rotation === null ? 'Multiple values' : undefined}
            onSelectionChange={key => {
              updateObstructions({ startLocation: key as 'floor' | 'ceiling' })
            }}
          >
            <SelectItem id="ceiling">From Ceiling</SelectItem>
            <SelectItem id="floor">From Floor</SelectItem>
          </Select>
          <NumberField
            label="Rotation"
            value={rotation ?? NaN}
            placeholder="Multiple values"
            minValue={0}
            maxValue={360}
            onChange={value => {
              updateObstructions({ rotation: { x: 0, y: 0, z: value } })
            }}
            formatOptions={{ style: 'unit', unit: 'degree', unitDisplay: 'narrow' }}
          />
          <DimensionInput
            label="Offset"
            value={offset ?? NaN}
            placeholder="Multiple values"
            onCommit={value => {
              updateObstructions({ offset: value })
            }}
          />
          <DimensionInput
            label="Width"
            value={width ?? NaN}
            placeholder="Multiple values"
            onCommit={value => {
              updateDimensions({ width: value })
            }}
          />
          <DimensionInput
            label="Length"
            value={length ?? NaN}
            placeholder="Multiple values"
            onCommit={value => {
              updateDimensions({ length: value })
            }}
          />
          <DimensionInput
            label="Height"
            value={height ?? NaN}
            placeholder="Multiple values"
            onCommit={value => {
              updateObstructions({ height: value })
            }}
          />
          {type === 'basic' && (
            <ColorPicker
              value={color ?? '#888888'}
              onChange={color => {
                updateObstructions({ color: color.toString('hex') })
              }}
            >
              <Label>Color</Label>
              <ChooserWell swatches={theme.colors.three.objects.obstruction.swatches} />
            </ColorPicker>
          )}
        </div>
      </PanelSection>
      <MetadataFields
        onChange={(field, value) => {
          dispatch(
            updateObjects(
              selectedObstructions.map(obstruction => ({
                id: obstruction.id,
                metadata: { ...obstruction.metadata, [field]: value },
              }))
            )
          )
        }}
        onDeleteImage={updated => {
          dispatch(updateObjects([{ id: updated.id, metadata: updated.metadata }]))
        }}
        selectedObjects={selectedObstructions}
      />
    </Panel>
  )
}
